/**
 * Локальный справочник типов операций компании (AK-527).
 *
 * Модель двухслойная и до этого PR имела только бэкенд:
 *   reference.operation_types      — платформенный каталог (общий для всех)
 *   company_operation_types (COT)  — слой тенанта: своё название, код 1С,
 *                                    доп. поля операции и доп. поля работы
 *
 * Тенант не мог завести НИ ОДНОГО своего типа из интерфейса: единственное
 * обращение фронта к `/company-operation-types` было GET'ом ради фильтра в
 * списке операций. Значит и код 1С (ключ будущей синхронизации, Блок 2), и
 * доп. поля («глубина обработки», «норма высева») завести было нечем.
 *
 * Эндпоинты — `backend/src/ikolhoz/api/v1/company_operation_types.py`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';

/** Описание доп. поля (совпадает с `FieldDefIn` на бэке). */
export interface ExtraFieldDef {
  key: string;
  type: string;
  label: string;
  unit?: string | null;
  required?: boolean | null;
  help?: string | null;
  min?: number | null;
  max?: number | null;
  options?: string[] | null;
}

/**
 * AK-523 / ADR-062 §O5 — уровень видимости тенант-справочника операций.
 * `company` — виден всем в холдинге; `cluster` — только своему кластеру;
 * `farm` — только своей ферме. `scope_ref_id` — id объекта уровня
 * (company_id / clusters.id / farms.id).
 */
export type CotScopeLevel = 'company' | 'cluster' | 'farm';

export interface CompanyOpType {
  id: string;
  company_id: string;
  ref_id?: string | null;
  ref_code?: string | null;
  ref_name_ru?: string | null;
  local_name: string;
  local_code_1c?: string | null;
  scope_level: CotScopeLevel;
  scope_ref_id?: string | null;
  overrides: Record<string, Record<string, unknown>>;
  extra_fields: ExtraFieldDef[];
  extra_work_fields: ExtraFieldDef[];
  /** ISO-timestamp, если строка архивная. Приходит только с include_archived=true. */
  archived_at?: string | null;
  /**
   * AK-564 — три факта, которых экрану-списку не хватало, чтобы читаться как
   * справочник культур: класс работы (наследуется от системной тех.операции),
   * ИМЯ области видимости и вес названия — сколько операций по нему заведено
   * за всё время (аналог колонки «Посевов»).
   */
  op_class?: string | null;
  scope_name?: string | null;
  usage_count?: number;
}

/** Платформенный тип операции — «родитель» локального. */
export interface ReferenceOpType {
  id: string;
  code: string;
  name_translations?: Record<string, string> | null;
  /**
   * Категория операции (`sowing | tillage | fertilizing | protection |
   * harvesting | irrigation | logistics | other`) — founder-locked домен,
   * миграция 121. Русские подписи для неё уже есть: `operationCategoryLabel`
   * в `platform-admin/reference/types.ts`. Второго маппера не заводим.
   */
  category?: string | null;
  /**
   * КЛАСС работы (`field | logistics | warehouse`) — миграция 093, резолвер
   * `reference.op_class_for`. Именно по нему решается, попадает ли операция в
   * историю поля. Это ДРУГОЕ поле, не `category`.
   */
  op_class?: string | null;
}

export interface CompanyOpTypeCreate {
  company_id: string;
  local_name: string;
  local_code_1c?: string | null;
  ref_id?: string | null;
  // AK-523: если не заданы — бэк проставит scope_level='company', scope_ref_id=company_id
  // (обратная совместимость с существующим UI до ферм-scoped справочника).
  scope_level?: CotScopeLevel;
  scope_ref_id?: string | null;
}

export interface CompanyOpTypeUpdate {
  local_name?: string;
  local_code_1c?: string | null;
  /**
   * Перепривязка к системной тех.операции (UAT founder 07.08). Бэкенд
   * разрешает её, только пока по названию НЕТ операций: у них зафиксирована
   * версия схемы полей. Есть операции — 409 с числом и альтернативой.
   */
  ref_id?: string | null;
  // scope_level / scope_ref_id — read-only после создания. Смена уровня
  // видимости = отдельная операция raise_scope (ADR §O8), не обычный PATCH.
}

/** Куда цепляется доп. поле: к самой операции или к строке работы. */
export type ExtraFieldScope = 'operation' | 'work';

const SCOPE_PATH: Record<ExtraFieldScope, string> = {
  operation: 'extra-fields',
  work: 'extra-work-fields',
};

export const companyOpTypeKeys = {
  all: () => ['company-operation-types'] as const,
  referenceTypes: () => ['reference', 'operation-types', 'all'] as const,
};

export function useCompanyOpTypes(opts: {
  includeArchived?: boolean;
} = {}): UseQueryResult<CompanyOpType[], Error> {
  const includeArchived = opts.includeArchived ?? false;
  return useQuery<CompanyOpType[], Error>({
    // Отдельный queryKey под include_archived, чтобы toggle не смешивал наборы
    // в кэше (иначе после выключения фильтра в списке остаются архивные).
    queryKey: [...companyOpTypeKeys.all(), { includeArchived }] as const,
    queryFn: () =>
      api.get<CompanyOpType[]>(
        `/api/v1/company-operation-types${includeArchived ? '?include_archived=true' : ''}`,
      ),
    staleTime: 60_000,
  });
}

/** Платформенный каталог — источник для привязки локального типа. */
export function useReferenceOpTypes(): UseQueryResult<ReferenceOpType[], Error> {
  return useQuery<ReferenceOpType[], Error>({
    queryKey: companyOpTypeKeys.referenceTypes(),
    queryFn: () => api.get<ReferenceOpType[]>('/api/v1/reference/operation-types'),
    staleTime: 10 * 60_000,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: companyOpTypeKeys.all() });
  };
}

export function useCreateCompanyOpType() {
  const invalidate = useInvalidate();
  return useMutation<CompanyOpType, Error, CompanyOpTypeCreate>({
    mutationFn: (body) =>
      api.post<CompanyOpType>('/api/v1/company-operation-types', body),
    onSuccess: invalidate,
  });
}

export function useUpdateCompanyOpType() {
  const invalidate = useInvalidate();
  return useMutation<CompanyOpType, Error, { id: string; body: CompanyOpTypeUpdate }>({
    mutationFn: ({ id, body }) =>
      api.patch<CompanyOpType>(`/api/v1/company-operation-types/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useArchiveCompanyOpType() {
  const invalidate = useInvalidate();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete<void>(`/api/v1/company-operation-types/${id}`),
    onSuccess: invalidate,
  });
}

export function useAddExtraField() {
  const invalidate = useInvalidate();
  return useMutation<
    CompanyOpType,
    Error,
    { id: string; scope: ExtraFieldScope; field: ExtraFieldDef }
  >({
    mutationFn: ({ id, scope, field }) =>
      api.post<CompanyOpType>(
        `/api/v1/company-operation-types/${id}/${SCOPE_PATH[scope]}`,
        field,
      ),
    onSuccess: invalidate,
  });
}

export function useRemoveExtraField() {
  const invalidate = useInvalidate();
  return useMutation<
    CompanyOpType,
    Error,
    { id: string; scope: ExtraFieldScope; key: string }
  >({
    mutationFn: ({ id, scope, key }) =>
      api.delete<CompanyOpType>(
        `/api/v1/company-operation-types/${id}/${SCOPE_PATH[scope]}/${encodeURIComponent(key)}`,
      ),
    onSuccess: invalidate,
  });
}

/** Русское имя платформенного типа (fallback — код). */
export function referenceTypeLabel(t: ReferenceOpType): string {
  return t.name_translations?.ru?.trim() || t.code;
}

// ────────────────────────── О-5 · Unarchive ─────────────────────────────────

export function useUnarchiveCompanyOpType() {
  const invalidate = useInvalidate();
  return useMutation<
    { id: string; archived_at: string | null },
    Error,
    string
  >({
    mutationFn: (id) =>
      api.post<{ id: string; archived_at: string | null }>(
        `/api/v1/company-operation-types/${id}/unarchive`,
        {},
      ),
    onSuccess: invalidate,
  });
}

// ────────────────────────── О-6 · Merge ─────────────────────────────────────

export interface OpTypeReferences {
  id: string;
  operations: number;
  company_work_norms: number;
  season_op_cost_lines: number;
}

export function useOpTypeReferences(id: string | null): UseQueryResult<
  OpTypeReferences,
  Error
> {
  return useQuery<OpTypeReferences, Error>({
    queryKey: ['company-operation-types', id, 'references'] as const,
    // Тянем счётчик только когда UI реально запросил превью — иначе на
    // каждой отрисовке строки таблицы был бы лишний запрос.
    enabled: id !== null,
    queryFn: () =>
      api.get<OpTypeReferences>(
        `/api/v1/company-operation-types/${id}/references`,
      ),
    staleTime: 10_000,
  });
}

export interface OpTypeMergePayload {
  target_id: string;
  source_ids: string[];
  dry_run?: boolean;
}

export interface OpTypeMergeResult {
  target_id: string;
  source_ids: string[];
  dry_run: boolean;
  per_source: Array<{
    source_id: string;
    target_id: string;
    dry_run: boolean;
    repointed: Record<string, number>;
    deleted_conflicts: Record<string, number>;
    total_repointed: number;
    total_deleted: number;
    archived: boolean;
    no_op: boolean;
    message: string;
  }>;
  archived: string[];
  total_repointed: number;
  total_deleted: number;
  message: string;
}

export function useMergeCompanyOpTypes() {
  const invalidate = useInvalidate();
  return useMutation<OpTypeMergeResult, Error, OpTypeMergePayload>({
    mutationFn: (body) =>
      api.post<OpTypeMergeResult>('/api/v1/company-operation-types/merge', body),
    // На успешном НЕ-dry-run список пересобираем — источники стали архивными,
    // счётчики operations уехали на survivor'а.
    //
    // ⚠️ dry_run список НЕ инвалидирует (AK-564). Раньше инвалидировал «проще,
    // чем условно», и это было безобидно, только пока предпросмотр запускали
    // кнопкой. Теперь счётчики считаются сами при выборе выжившего — а
    // инвалидация меняет ссылку на массив выбранных строк, что снова запускает
    // предпросмотр: получилась бы бесконечная петля запросов.
    onSuccess: (_result, vars) => {
      if (!vars.dry_run) invalidate();
    },
  });
}

// ────────────────────────── О-7 · Import queue ──────────────────────────────

export interface ImportQueueItem {
  id: string;
  incoming_name: string;
  source_system?: string | null;
  note?: string | null;
  company_op_type_id?: string | null;
  status: 'pending' | 'mapped' | 'created' | 'rejected';
  resolved_ref_id?: string | null;
  occurrence_count: number;
}

export function useImportQueue(
  status: ImportQueueItem['status'] | 'all' = 'pending',
): UseQueryResult<ImportQueueItem[], Error> {
  // `status=all` бэкенд понимает явно (AK-564). Пустой qs НЕ равен «все»:
  // у ручки дефолт `pending`, и «все» молча превращалось бы в «висящие».
  const qs = `?status=${status}`;
  return useQuery<ImportQueueItem[], Error>({
    queryKey: ['company-operation-types', 'import-queue', status] as const,
    queryFn: () =>
      api.get<ImportQueueItem[]>(
        `/api/v1/company-operation-types/import-queue${qs}`,
      ),
    staleTime: 30_000,
  });
}

export function useRequestOpTypeImport() {
  const qc = useQueryClient();
  return useMutation<
    ImportQueueItem,
    Error,
    { incoming_name: string; company_op_type_id?: string | null; note?: string }
  >({
    mutationFn: (body) =>
      api.post<ImportQueueItem>(
        '/api/v1/company-operation-types/request-import',
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['company-operation-types', 'import-queue'],
      });
    },
  });
}


/**
 * Класс работы системной тех.операции — «что это за работа».
 *
 * Домен ровно из миграции 093: `field | logistics | warehouse`. Решение founder
 * Ж (07.08): логистика — полноценная тех.операция, просто не полевая; в полевых
 * экранах не показывается, всплывает в бюджетном контексте. По `op_class`
 * (а не по `category`) 093-я и решает, идёт ли операция в историю поля.
 *
 * Неизвестное значение НЕ печатаем латиницей: если домен расширят, экран честно
 * скажет «класс не определён», а не покажет `warehouse` агроному.
 */
export function referenceOpClassLabel(opClass: string | null | undefined): string | null {
  switch (opClass) {
    case 'field':
      return 'Полевая операция';
    case 'logistics':
      return 'Логистика';
    case 'warehouse':
      return 'Складская операция';
    default:
      return null;
  }
}

/** «Весь холдинг» / «Кластер» / «Хозяйство» — уровень видимости названия. */
export function cotScopeLabel(level: CotScopeLevel): string {
  switch (level) {
    case 'company':
      return 'Весь холдинг';
    case 'cluster':
      return 'Кластер';
    case 'farm':
      return 'Одно хозяйство';
    default:
      return level;
  }
}

/**
 * Колонка «Видно» — теми же словами, что «Видна» у культур (AK-564).
 *
 * Решение founder 5 (10.08): слово «ферма» в интерфейсе не употребляем — у нас
 * «хозяйство»; в коде и API `farm` остаётся. И область называем ИМЕНЕМ: бейдж
 * «кластер» без имени не отвечает на вопрос «увидит ли это Заречное».
 */
export function cotVisibilityLabel(item: {
  scope_level: CotScopeLevel;
  scope_name?: string | null;
}): string {
  if (item.scope_level === 'company') return 'Весь холдинг';
  const name = item.scope_name?.trim();
  if (item.scope_level === 'cluster') {
    return name ? `Кластер «${name}»` : 'Кластер';
  }
  return name ?? 'Одно хозяйство';
}

/**
 * Класс работы для чипа-фильтра — коротко и во множественном числе.
 *
 * ⚠️ Домен здесь РЕАЛЬНЫЙ (`field | logistics | warehouse`, миграция 093), а не
 * «механизированные / ручные / услуги подрядчика» из макета: таких значений в
 * модели нет и заводить их ради подписи чипа значило бы придумать домен под
 * картинку. Расхождение отмечено в PR.
 */
export function opClassChipLabel(opClass: string | null | undefined): string | null {
  switch (opClass) {
    case 'field':
      return 'Полевые';
    case 'logistics':
      return 'Логистика';
    case 'warehouse':
      return 'Складские';
    default:
      return null;
  }
}

/** «412 операций» / «1 операция» / «пока не использовалось». */
export function operationsCountLabel(n: number): string {
  if (n <= 0) return 'пока не использовалось';
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word = 'операций';
  if (mod10 === 1 && mod100 !== 11) word = 'операция';
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) word = 'операции';
  return `${n} ${word}`;
}

/** «+3 своих поля» — тихая подпись под названием (макет, сост. 2). */
export function ownFieldsLabel(item: CompanyOpType): string | null {
  const n = (item.extra_fields?.length ?? 0) + (item.extra_work_fields?.length ?? 0);
  if (n <= 0) return null;
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word = 'полей';
  if (mod10 === 1 && mod100 !== 11) word = 'поле';
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) word = 'поля';
  return `+${n} ${word === 'поле' ? 'своё поле' : `своих ${word}`}`;
}
